/**
 * Usage Tracker — SeeTex Web
 *
 * Tracks all user sessions (including anonymous/guest) in Firestore
 * under `stats/active-users/sessions/{uid}`. Session docs persist
 * after the user leaves so we retain historical data.
 *
 * Also mirrors current presence into `stats/recent-active/users/{uid}`.
 * Those docs are intended to represent users seen within the last 10 minutes,
 * using an `expiresAt` timestamp refreshed on each heartbeat.
 *
 * Each session doc contains:
 *   - lastSeen:    server timestamp (updated every 5 min)
 *   - firstSeen:   server timestamp (set once on first heartbeat)
 *   - isAnonymous: whether the user is a guest
 *   - engagement:  count of meaningful actions (edits, scans, doc creates)
 *   - active:      whether the session is currently live
 */

import {
    doc,
    setDoc,
    collection,
    getDocs,
    getDoc,
    query,
    where,
    Timestamp,
    increment,
    serverTimestamp,
} from "firebase/firestore";
import { getFirebaseFirestore, isDemoMode } from "../config/firebase";

export interface ActiveUserWindowStat {
    minutes: number;
    count: number;
}

const ACTIVE_THRESHOLD_MS = 15 * 60 * 1000; // 15 minutes
const RECENT_ACTIVE_WINDOW_MS = 10 * 60 * 1000; // 10 minutes
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

function recentActiveRef(uid: string) {
    const db = getFirebaseFirestore();
    return doc(db, "stats", "recent-active", "users", uid);
}

function sessionRef(uid: string) {
    const db = getFirebaseFirestore();
    return doc(db, "stats", "active-users", "sessions", uid);
}

let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let currentUid: string | null = null;
let currentIsAnonymous = false;
let sessionCounted = false;
let firstHeartbeat = true;

/**
 * Start tracking a user's presence. Call this after auth state changes.
 */
export function startTracking(uid: string, isAnonymous: boolean): void {
    if (isDemoMode()) return;

    // Clean up previous session if uid changed
    if (currentUid && currentUid !== uid) {
        stopTracking();
    }

    currentUid = uid;
    currentIsAnonymous = isAnonymous;
    sessionCounted = false;
    firstHeartbeat = true;
    sendHeartbeat(uid);

    // Send heartbeats periodically
    if (!heartbeatTimer) {
        heartbeatTimer = setInterval(() => {
            if (currentUid) sendHeartbeat(currentUid);
        }, HEARTBEAT_INTERVAL_MS);
    }
}

/**
 * Stop tracking — mark session inactive (doc is kept for historical insight).
 */
export function stopTracking(): void {
    if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
    }

    if (currentUid && !isDemoMode()) {
        setDoc(
            sessionRef(currentUid),
            { active: false, lastSeen: serverTimestamp() },
            { merge: true }
        ).catch(() => {});
        setDoc(
            recentActiveRef(currentUid),
            {
                active: false,
                lastSeen: serverTimestamp(),
                expiresAt: Timestamp.fromMillis(Date.now()),
            },
            { merge: true }
        ).catch(() => {});
    }

    currentUid = null;
    currentIsAnonymous = false;
    sessionCounted = false;
    firstHeartbeat = true;
}

/**
 * Record a meaningful user action (document edit, scan, doc create, etc.).
 * Call this from wherever the user does something real in the app.
 */
export function trackEngagement(): void {
    if (!currentUid || isDemoMode()) return;

    setDoc(
        sessionRef(currentUid),
        { engagement: increment(1), lastSeen: serverTimestamp() },
        { merge: true }
    ).catch(() => {});
    setDoc(
        recentActiveRef(currentUid),
        {
            engagement: increment(1),
            lastSeen: serverTimestamp(),
            active: true,
            isAnonymous: currentIsAnonymous,
            expiresAt: Timestamp.fromMillis(Date.now() + RECENT_ACTIVE_WINDOW_MS),
        },
        { merge: true }
    ).catch(() => {});
}

/**
 * Write a heartbeat for this user session.
 */
async function sendHeartbeat(uid: string): Promise<void> {
    try {
        const recentActivePayload = {
            lastSeen: serverTimestamp(),
            active: true,
            isAnonymous: currentIsAnonymous,
            expiresAt: Timestamp.fromMillis(Date.now() + RECENT_ACTIVE_WINDOW_MS),
        };

        if (firstHeartbeat) {
            firstHeartbeat = false;
            // First heartbeat: set metadata fields, merge so we don't clobber
            // engagement from a previous visit with the same anonymous UID.
            await setDoc(sessionRef(uid), {
                lastSeen: serverTimestamp(),
                firstSeen: serverTimestamp(),
                isAnonymous: currentIsAnonymous,
                active: true,
                engagement: increment(0), // initialize if missing, keep if exists
            }, { merge: true });
            await setDoc(recentActiveRef(uid), {
                ...recentActivePayload,
                firstSeen: serverTimestamp(),
                engagement: increment(0),
            }, { merge: true });
        } else {
            await setDoc(sessionRef(uid), {
                lastSeen: serverTimestamp(),
                active: true,
            }, { merge: true });
            await setDoc(recentActiveRef(uid), recentActivePayload, { merge: true });
        }

        // Increment total-visits counter only once per session
        if (!sessionCounted) {
            sessionCounted = true;
            const db = getFirebaseFirestore();
            await setDoc(
                doc(db, "stats", "counters"),
                { totalSessions: increment(1) },
                { merge: true }
            );
        }
    } catch (e) {
        // Don't let tracking failures break the app
        console.warn("[SeeTex] Heartbeat failed:", (e as Error).message);
    }
}

/**
 * Get the count of users seen within the last 10 minutes from the dedicated
 * recent-active collection.
 */
export async function getRecentActiveUserCount(): Promise<number> {
    if (isDemoMode()) return 0;

    try {
        const db = getFirebaseFirestore();
        const cutoff = Timestamp.fromMillis(Date.now());
        const usersRef = collection(db, "stats", "recent-active", "users");
        const q = query(usersRef, where("expiresAt", ">=", cutoff));
        const snapshot = await getDocs(q);
        return snapshot.size;
    } catch {
        return 0;
    }
}

/**
 * Get active-user counts for multiple rolling windows based on session lastSeen.
 */
export async function getActiveUserCountsForWindows(windowsInMinutes: number[]): Promise<ActiveUserWindowStat[]> {
    if (isDemoMode()) {
        return windowsInMinutes.map((minutes) => ({ minutes, count: 0 }));
    }

    const normalized = [...new Set(windowsInMinutes)]
        .filter((minutes) => Number.isFinite(minutes) && minutes > 0)
        .sort((a, b) => a - b);

    if (normalized.length === 0) return [];

    try {
        const db = getFirebaseFirestore();
        const now = Date.now();
        const maxWindowMs = normalized[normalized.length - 1] * 60 * 1000;
        const cutoff = Timestamp.fromMillis(now - maxWindowMs);
        const sessionsRef = collection(db, "stats", "active-users", "sessions");
        const q = query(sessionsRef, where("lastSeen", ">=", cutoff));
        const snapshot = await getDocs(q);

        const counts = normalized.map((minutes) => ({
            minutes,
            count: 0,
        }));

        for (const sessionDoc of snapshot.docs) {
            const lastSeen = sessionDoc.data().lastSeen;
            const lastSeenMs = lastSeen instanceof Timestamp ? lastSeen.toMillis() : null;
            if (lastSeenMs === null) continue;
            const ageMs = now - lastSeenMs;

            for (const stat of counts) {
                if (ageMs <= stat.minutes * 60 * 1000) {
                    stat.count += 1;
                }
            }
        }

        return counts;
    } catch {
        return normalized.map((minutes) => ({ minutes, count: 0 }));
    }
}

/**
 * Get the count of currently active users (lastSeen within threshold).
 */
export async function getActiveUserCount(): Promise<number> {
    if (isDemoMode()) return 0;

    try {
        const db = getFirebaseFirestore();
        const cutoff = Timestamp.fromMillis(Date.now() - ACTIVE_THRESHOLD_MS);
        const sessionsRef = collection(db, "stats", "active-users", "sessions");
        const q = query(sessionsRef, where("lastSeen", ">=", cutoff));
        const snapshot = await getDocs(q);
        return snapshot.size;
    } catch {
        return 0;
    }
}

/**
 * Get total number of sessions ever (cumulative).
 */
export async function getTotalSessionCount(): Promise<number> {
    if (isDemoMode()) return 0;

    try {
        const db = getFirebaseFirestore();
        const countersDoc = await getDoc(doc(db, "stats", "counters"));
        return countersDoc.exists() ? (countersDoc.data().totalSessions ?? 0) : 0;
    } catch {
        return 0;
    }
}
